import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FileSpreadsheet, FileText, Loader2, RefreshCcw, Search, Store } from 'lucide-react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import OrderAttachmentModal from '../components/OrderAttachmentModal';
import { fetchOrderShopReport } from '../services/reportService';
import { updateOrderShopSettlementStatus } from '../services/orderService';
import { fetchShops } from '../services/shopService';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
});

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return currencyFormatter.format(0);
  return currencyFormatter.format(numeric);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending (All non-delivered)' },
  { value: 'delivered', label: 'Delivered Shops' },
  { value: '1', label: 'Placed' },
  { value: '2', label: 'Informed to Shop' },
  { value: '3', label: 'Ready for Pickup' },
  { value: '4', label: 'Delivery Boy Assigned' },
  { value: '5', label: 'Picked Up' },
  { value: '6', label: 'Delivered' },
  { value: '7', label: 'Cancelled' },
];

const DATE_FIELD_OPTIONS = [
  { value: 'orderCreated', label: 'Order Created Date' },
  { value: 'orderDelivered', label: 'Order Delivered Date' },
  { value: 'shopDelivered', label: 'Shop Delivered Date' },
];

const DEFAULT_PAGE_SIZE = 20;

export default function ShopOrderReport() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({
    totalOrderShops: 0,
    totalShopAmount: 0,
    totalShopFinalAmount: 0,
    totalOrders: 0,
    totalOrderAmount: 0,
    totalDeliveryCharge: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [shopId, setShopId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateField, setDateField] = useState('orderCreated');
  const [loading, setLoading] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [shops, setShops] = useState([]);
  const [attachmentOrder, setAttachmentOrder] = useState(null);
  const [settlementUpdating, setSettlementUpdating] = useState(() => new Set());

  useEffect(() => {
    const loadShops = async () => {
      try {
        const response = await fetchShops({ page: 1, limit: 200, sortBy: 'name', sortOrder: 'ASC' });
        const data = response.data?.data;
        const list = data?.records || data || [];
        setShops(list);
      } catch (error) {
        toast.error('Failed to load shops');
      }
    };
    loadShops();
  }, []);

  const queryParams = useMemo(() => {
    const params = {
      page,
      limit: DEFAULT_PAGE_SIZE,
      dateField,
    };
    const trimmedSearch = search.trim();
    if (trimmedSearch) params.search = trimmedSearch;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (shopId) params.shopId = shopId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }, [dateField, endDate, page, search, shopId, startDate, statusFilter]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await fetchOrderShopReport(queryParams);
      const data = response.data?.data || {};
      setRecords(data.records || []);
      setSummary({
        totalOrderShops: data.summary?.totalOrderShops || 0,
        totalShopAmount: data.summary?.totalShopAmount || 0,
        totalShopFinalAmount: data.summary?.totalShopFinalAmount || 0,
        totalOrders: data.summary?.totalOrders || 0,
        totalOrderAmount: data.summary?.totalOrderAmount || 0,
        totalDeliveryCharge: data.summary?.totalDeliveryCharge || 0,
      });
      setTotalPages(data.pages || 1);
    } catch (error) {
      toast.error('Failed to load shop order report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
    if (value === 'delivered' || value === '6') {
      setDateField('shopDelivered');
    }
  };

  const handleShopChange = (event) => {
    setShopId(event.target.value);
    setPage(1);
  };

  const handleDateFieldChange = (event) => {
    setDateField(event.target.value);
    setPage(1);
  };

const exportHeaders = [
  'Order ID',
  'Customer',
  'Customer Phone',
  'Shop',
  'Shop Status',
  'Delivery Executive',
  'Shop Total',
  'Shop Final',
  'Settlement Paid',
  'Order Created',
];

const escapeHtml = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const mapExportRows = (records) =>
  records.map((record) => ({
    orderId: record.order?.showId || '',
    customer: record.order?.customer?.name || '',
    customerPhone: record.order?.customer?.phone || '',
    shop: record.shop?.name || '',
    status: record.currentStatus?.name || '',
    deliveryBoy: record.deliveryBoy?.name || '',
    shopTotal: Number(record.shopTotal ?? record.shop_total ?? 0),
    shopFinal: Number(record.finalAmount ?? record.final_amount ?? 0),
    settlementPaid: record.settlementPaid ? 'Yes' : 'No',
    orderCreated: formatDateTime(record.order?.createdAt),
  }));

  const handleDateChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const formatCsvValue = (value) => {
    if (value == null) return '';
    const stringValue = String(value);
    const escapedValue = stringValue.replace(/"/g, '""');
    return /[",\r\n]/.test(stringValue) ? `"${escapedValue}"` : escapedValue;
  };

  const buildCsv = (rows) =>
    rows
      .map((row) => row.map((value) => formatCsvValue(value ?? '')).join(','))
      .join('\r\n');

  const handleExportCsv = async () => {
    setCsvExporting(true);
    try {
      const exportParams = { ...queryParams, export: true, page: 1 };
      const response = await fetchOrderShopReport(exportParams);
      const rows = response.data?.data?.records || [];
      if (!rows.length) {
        toast.info('No records available to export');
        return;
      }
      const header = exportHeaders;
      const exportRows = mapExportRows(rows);
      const body = exportRows.map((row) => [
        row.orderId,
        row.customer,
        row.customerPhone,
        row.shop,
        row.status,
        row.deliveryBoy,
        row.shopTotal,
        row.shopFinal,
        row.settlementPaid,
        row.orderCreated,
      ]);
      const csvContent = buildCsv([header, ...body]);
      const blob = new Blob(['\ufeff', csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'shop-order-report.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setCsvExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setPdfExporting(true);
    try {
      const exportParams = { ...queryParams, export: true, page: 1 };
      const response = await fetchOrderShopReport(exportParams);
      const rows = response.data?.data?.records || [];
      if (!rows.length) {
        toast.info('No records available to export');
        return;
      }

      const exportRows = mapExportRows(rows);
      const tableHead = `<tr>${exportHeaders
        .map((header) => `<th>${escapeHtml(header)}</th>`)
        .join('')}</tr>`;
      const tableBody = exportRows
        .map(
          (row) => {
            const renderCell = (value, fallback = '-') => {
              const safe = escapeHtml(value ?? '');
              return safe || `<span class="muted">${escapeHtml(fallback)}</span>`;
            };

            return `<tr>
              <td>${renderCell(row.orderId)}</td>
              <td>${renderCell(row.customer)}</td>
              <td>${renderCell(row.customerPhone)}</td>
              <td>${renderCell(row.shop)}</td>
              <td>${renderCell(row.status)}</td>
              <td>${renderCell(row.deliveryBoy)}</td>
              <td>${renderCell(formatCurrency(row.shopTotal))}</td>
              <td>${renderCell(formatCurrency(row.shopFinal))}</td>
              <td>${renderCell(row.settlementPaid, 'No')}</td>
              <td>${renderCell(row.orderCreated)}</td>
            </tr>`;
          }
        )
        .join('');

      const summaryData = response.data?.data?.summary || {};
      const summaryItems = [
        {
          label: 'Total Shop Orders',
          value: summaryData.totalOrderShops ?? rows.length ?? 0,
        },
        {
          label: 'Total Shop Value',
          value: formatCurrency(summaryData.totalShopAmount ?? 0),
        },
        {
          label: 'Total Shop Final',
          value: formatCurrency(summaryData.totalShopFinalAmount ?? 0),
        },
        {
          label: 'Total Orders',
          value: summaryData.totalOrders ?? rows.length ?? 0,
        },
      ];

      const summaryCards = summaryItems
        .map(
          (item) => `
            <div class="summary-card">
              <div class="summary-label">${escapeHtml(item.label)}</div>
              <div class="summary-value">${escapeHtml(item.value)}</div>
            </div>
          `,
        )
        .join('');

      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (!printWindow) {
        toast.error('Unable to open print window. Please allow pop-ups for this site.');
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Shop Order Report</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
              :root {
                color-scheme: light;
              }
              * {
                box-sizing: border-box;
              }
              body {
                font-family: 'Inter', Arial, sans-serif;
                margin: 0;
                padding: 0;
                background: #f3f4f6;
                color: #111827;
              }
              .container {
                max-width: 1100px;
                margin: 0 auto;
                padding: 32px 40px;
                background: #ffffff;
                box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
                border-radius: 24px;
              }
              .header {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 28px;
              }
              .title {
                font-size: 26px;
                font-weight: 700;
                letter-spacing: -0.02em;
                margin: 0;
              }
              .timestamp {
                font-size: 13px;
                color: #6b7280;
              }
              .summary-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                gap: 16px;
                margin-bottom: 28px;
              }
              .summary-card {
                border-radius: 18px;
                padding: 16px 18px;
                background: linear-gradient(135deg, rgba(79, 70, 229, 0.12), rgba(59, 130, 246, 0.08));
                border: 1px solid rgba(99, 102, 241, 0.16);
              }
              .summary-label {
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                color: #4b5563;
                margin-bottom: 6px;
              }
              .summary-value {
                font-size: 18px;
                font-weight: 600;
                color: #1f2937;
              }
              table {
                width: 100%;
                border-collapse: separate;
                border-spacing: 0;
                border-radius: 18px;
                overflow: hidden;
                border: 1px solid #e5e7eb;
              }
              thead th {
                background: linear-gradient(135deg, #eef2ff, #e0f2fe);
                color: #1e3a8a;
                font-weight: 600;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                padding: 12px 14px;
                border-bottom: 1px solid #dbeafe;
              }
              tbody tr:nth-child(even) {
                background: #f9fafb;
              }
              tbody td {
                padding: 12px 14px;
                font-size: 13px;
                color: #1f2937;
                border-bottom: 1px solid #e5e7eb;
              }
              tbody tr:last-child td {
                border-bottom: none;
              }
              .muted {
                color: #9ca3af;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h1 class="title">Shop Order Report</h1>
                <div class="timestamp">Generated on ${escapeHtml(new Date().toLocaleString())}</div>
              </div>
              <div class="summary-grid">
                ${summaryCards}
              </div>
              <table>
                <thead>${tableHead}</thead>
                <tbody>${tableBody}</tbody>
              </table>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 300);
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setPdfExporting(false);
    }
  };

  const openAttachments = (record) => {
    const orderPayload = {
      showId: record.order?.showId,
      pickupAttachments: record.order?.pickupAttachments || [],
      shops: [
        {
          id: record.id,
          shop: record.shop,
          deliveredAt: record.deliveredAt,
          attachments: record.attachments || [],
        },
      ],
    };
    setAttachmentOrder(orderPayload);
  };

  const closeAttachments = () => setAttachmentOrder(null);

  const toggleSettlementUpdating = (orderShopId, busy) => {
    setSettlementUpdating((prev) => {
      const next = new Set(prev);
      if (busy) {
        next.add(orderShopId);
      } else {
        next.delete(orderShopId);
      }
      return next;
    });
  };

  const handleSettlementPaidChange = async (record, nextValue) => {
    const orderId = record.orderId || record.order?.id;
    if (!orderId) {
      toast.error('Missing order information to update settlement status');
      return;
    }

    toggleSettlementUpdating(record.id, true);

    try {
      const response = await updateOrderShopSettlementStatus(orderId, record.id, nextValue);
      setRecords((prev) =>
        prev.map((item) => (item.id === record.id ? { ...item, settlementPaid: nextValue } : item)),
      );
      const apiMessage = response?.data?.message;
      toast.success(
        apiMessage
          || (nextValue ? 'Order marked as paid for settlement' : 'Order marked as unpaid for settlement'),
      );
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || 'Failed to update settlement status';
      toast.error(message);
    } finally {
      toggleSettlementUpdating(record.id, false);
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Shop Order Report</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              View order fulfilment performance across shops with exportable data.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadReport}
              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50"
              disabled={loading}
            >
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCcw size={16} className="mr-2" />}
              Refresh
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-500"
              disabled={csvExporting}
            >
              {csvExporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileSpreadsheet size={16} className="mr-2" />}
              Export CSV
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-500"
              disabled={pdfExporting}
            >
              {pdfExporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileText size={16} className="mr-2" />}
              Export PDF
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by order ID"
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Shop</div>
              <div className="relative">
                <Store size={16} className="absolute left-3 top-3 text-gray-400" />
                <select
                  value={shopId}
                  onChange={handleShopChange}
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Shops</option>
                  {shops.map((shop) => (
                    <option key={shop.id} value={shop.id}>
                      {shop.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Status</div>
              <select
                value={statusFilter}
                onChange={(event) => handleStatusChange(event.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Start Date</div>
              <input
                type="date"
                value={startDate}
                onChange={handleDateChange(setStartDate)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">End Date</div>
              <input
                type="date"
                value={endDate}
                onChange={handleDateChange(setEndDate)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Date Field</div>
              <select
                value={dateField}
                onChange={handleDateFieldChange}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {DATE_FIELD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-800 rounded-xl p-4">
              <p className="text-xs text-indigo-600 dark:text-indigo-300 uppercase tracking-wide">Shop Orders</p>
              <p className="text-xl font-semibold text-indigo-700 dark:text-indigo-200">{summary.totalOrderShops}</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
              <p className="text-xs text-blue-600 dark:text-blue-300 uppercase tracking-wide">Shop Value</p>
              <p className="text-xl font-semibold text-blue-700 dark:text-blue-200">{formatCurrency(summary.totalShopAmount)}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 border border-green-100 dark:border-green-800 rounded-xl p-4">
              <p className="text-xs text-green-600 dark:text-green-300 uppercase tracking-wide">Order Delivery Charges</p>
              <p className="text-xl font-semibold text-green-700 dark:text-green-200">{formatCurrency(summary.totalDeliveryCharge)}</p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800 rounded-xl p-4">
              <p className="text-xs text-emerald-600 dark:text-emerald-300 uppercase tracking-wide">Order Value</p>
              <p className="text-xl font-semibold text-emerald-700 dark:text-emerald-200">{formatCurrency(summary.totalOrderAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Order ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Shop</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Delivery Executive</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Shop Total</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Shop Final</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Order Created</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Settlement Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Attachments</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-10 text-center text-sm text-gray-500">
                      <Loader2 size={24} className="mx-auto animate-spin text-indigo-500" />
                      <p className="mt-2">Loading report...</p>
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-10 text-center text-sm text-gray-500">
                      No records found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  records.map((record) => {
                    const hasAttachments =
                      (Array.isArray(record.attachments) && record.attachments.length > 0) ||
                      (Array.isArray(record.order?.pickupAttachments) && record.order.pickupAttachments.length > 0);

                    return (
                      <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{record.order?.showId || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{record.shop?.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{record.currentStatus?.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{record.deliveryBoy?.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(record.shopTotal ?? record.shop_total)}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(record.finalAmount ?? record.final_amount)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDateTime(record.order?.createdAt)}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <input
                            id={`settlement-paid-${record.id}`}
                            type="checkbox"
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            checked={Boolean(record.settlementPaid)}
                            onChange={(event) => handleSettlementPaidChange(record, event.target.checked)}
                            disabled={settlementUpdating.has(record.id)}
                            aria-label="Toggle settlement paid"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {hasAttachments ? (
                            <button
                              type="button"
                              onClick={() => openAttachments(record)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full border border-indigo-100 dark:border-indigo-700 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-800/40"
                            >
                              View Attachments
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/60 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1 || loading}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages || loading}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      {attachmentOrder && <OrderAttachmentModal order={attachmentOrder} onClose={closeAttachments} />}
    </LayoutWithSidebar>
  );
}
